"use client";

import BlokWrap from "@/components/bloks/BlokWrap";
import { Button } from "@/components/ui/Button";
import { RichTextRender } from "@/components/ui/RichTextRender";
import { AccountModalTrigger } from "@/features/account/AccountModal";
import { useGlobal } from "@/features/global/GlobalContext";
import { trackContentClick } from "@/lib/analytics/trackContentClick";
import { sbLink } from "@/lib/storyblok/link";
import type { BlokAccessBanner } from "@/storyblok";
import { Portal } from "@ariakit/react";
import type { BlokProps } from "@frend-digital/storyblok";
import { clsx } from "@frend-digital/ui";
import { useHydrated, useMediaQuery } from "@frend-digital/ui/hooks";
import { useEffect, useRef } from "react";
import type { StoryblokMultilink } from "storyblok";
import { AssetBlok, sizes } from "../Asset";
import styles from "./AccessBanner.module.css";

export const AccessBannerBlok = ({ blok, preview }: BlokProps<BlokAccessBanner>) => {
	const bannerRef = useRef<HTMLDivElement>(null);
	const hydrated = useHydrated();
	const isMobile = useMediaQuery("(max-width: 1024px)");

	const {
		data: { colorMap },
	} = useGlobal();

	const colorEntry = colorMap[blok.backgroundColor!] || "#c1e66a";

	useEffect(() => {
		const plp = document.querySelector<HTMLElement>(".product-listing-page");
		if (!plp) return;
		plp.inert = true;

		const plpTitleElement = document.querySelector("[data-plp-title]");
		if (plpTitleElement) {
			setTimeout(() => {
				const elementRect = plpTitleElement.getBoundingClientRect();
				const absoluteElementTop = elementRect.top + window.scrollY;

				window.scrollTo({
					top: absoluteElementTop - 16,
					behavior: "instant",
				});
			}, 100);
		}

		return () => {
			plp.inert = false;
		};
	}, []);

	if (!hydrated) return null;
	return (
		<Portal>
			<BlokWrap editable={blok} as="div" asChild>
				<div
					ref={bannerRef}
					className={styles.content}
					style={{ backgroundColor: colorEntry.color || "#c1e66a" }}
				>
					<div className={clsx(styles.side)}>
						<div className={styles.info} style={{ color: colorEntry.foreground }}>
							<h2>{blok.title}</h2>
							{blok.description && <RichTextRender blok={blok.description} preview={preview} />}
							<div className={styles.buttonGroup}>
								{blok.buttons?.map((button, index) => {
									const link = sbLink(button.link as StoryblokMultilink);
									return (
										<AccountModalTrigger
											// biome-ignore lint/suspicious/noArrayIndexKey: <explanation>
											key={blok._uid + index}
											asChild
											mode={index === 1 ? "register" : "login"}
											onClick={(e) => e.stopPropagation()}
										>
											<Button
												className={index > 0 ? styles.button : undefined}
												href={link?.href}
												onClick={
													link?.href
														? () =>
																trackContentClick({
																	contentType: blok.component,
																	itemId: blok._uid,
																	href: link.href,
																	label: button.text,
																})
														: undefined
												}
												style={{
													backgroundColor:
														typeof button.color === "string" ? button.color : undefined,
													color:
														typeof button.outlineColor === "string"
															? button.outlineColor
															: undefined,
													border: index > 0 ? `1px solid ${colorEntry.foreground}` : "none",
												}}
											>
												{button.text}
											</Button>
										</AccountModalTrigger>
									);
								})}
							</div>
						</div>
					</div>
					{!isMobile && (
						<div className={clsx(styles.side)}>
							{blok.media && <AssetBlok source={blok.media} sizes={sizes(50, 100)} />}
						</div>
					)}
					<style>
						{`
						.product-card-price {
  						   display: none;
						}
					    [data-plp-content] {
						   pointer-events: none;
						}

						.product-card {
						   pointer-events: none;
						}
					`}
					</style>
				</div>
			</BlokWrap>
		</Portal>
	);
};
